import React, { useEffect, useState, useRef } from "react";
import { useSearchParams } from "react-router-dom";
import { api, mediaUrl, BACKEND_URL, getToken } from "../lib/api";
import { useAuth } from "../lib/auth";
import { Send } from "lucide-react";

const Avatar = ({ u, size = 40 }) => (
    <div className="rounded-full overflow-hidden border border-gold bg-surface flex items-center justify-center shrink-0" style={{ width: size, height: size }}>
        {u?.profile?.avatar_url ? <img src={mediaUrl(u.profile.avatar_url)} alt="" className="w-full h-full object-cover" /> : <span className="text-ink text-sm font-medium">{u?.full_name?.[0]?.toUpperCase()}</span>}
    </div>
);

const Messages = () => {
    const { user: me } = useAuth();
    const [params] = useSearchParams();
    const initialTo = params.get("to");
    const [convs, setConvs] = useState([]);
    const [active, setActive] = useState(null);
    const [messages, setMessages] = useState([]);
    const [text, setText] = useState("");
    const wsRef = useRef(null);
    const endRef = useRef(null);

    const loadConvs = async () => {
        try {
            const { data } = await api.get("/conversations");
            setConvs(data);
        } catch {}
    };

    const loadMessages = async (userId) => {
        try {
            const { data } = await api.get(`/messages/${userId}`);
            setMessages(data);
            setTimeout(() => endRef.current?.scrollIntoView({ behavior: "smooth" }), 50);
        } catch {}
    };

    useEffect(() => {
        loadConvs();
        const t = getToken();
        if (t) {
            const wsUrl = `${BACKEND_URL.replace(/^http/, "ws")}/api/ws?token=${t}`;
            try {
                const ws = new WebSocket(wsUrl);
                wsRef.current = ws;
                ws.onmessage = (ev) => {
                    try {
                        const data = JSON.parse(ev.data);
                        if (data.type === "message") {
                            setMessages((prev) => [...prev, data.data]);
                            loadConvs();
                        }
                    } catch {}
                };
            } catch {}
        }
        return () => wsRef.current?.close();
    }, []);

    useEffect(() => {
        if (initialTo) {
            (async () => {
                try {
                    const { data } = await api.get(`/profile/${initialTo}`);
                    setActive({ id: data.id, full_name: data.full_name, profile: data.profile });
                    loadMessages(data.id);
                } catch {}
            })();
        }
    }, [initialTo]);

    const openConv = (c) => {
        setActive(c.user);
        loadMessages(c.user.id);
    };

    const send = async () => {
        if (!text.trim() || !active) return;
        const t = text;
        setText("");
        try {
            const { data } = await api.post("/messages", { to_user_id: active.id, text: t });
            setMessages((p) => [...p, data]);
            loadConvs();
            setTimeout(() => endRef.current?.scrollIntoView({ behavior: "smooth" }), 50);
        } catch {}
    };

    return (
        <div className="grid md:grid-cols-[300px_1fr] gap-4 h-[calc(100vh-8rem)]" data-testid="messages-page">
            {/* Conv list */}
            <div className="bg-paper rounded-2xl border border-border-line overflow-hidden">
                <div className="px-4 py-3 border-b border-border-line">
                    <h2 className="font-heading text-xl">Conversazioni</h2>
                </div>
                <div className="overflow-y-auto h-full">
                    {convs.length === 0 && <div className="p-6 text-center text-sm text-muted">Nessuna conversazione</div>}
                    {convs.map((c) => (
                        <button key={c.conv_id} onClick={() => openConv(c)} className={`w-full text-left px-4 py-3 flex gap-3 hover:bg-surface transition-colors ${active?.id === c.user?.id ? "bg-surface" : ""}`}>
                            <Avatar u={c.user} />
                            <div className="min-w-0 flex-1">
                                <div className="font-medium text-sm truncate">{c.user?.full_name}</div>
                                <div className="text-xs text-muted truncate">{c.last_message?.text}</div>
                            </div>
                        </button>
                    ))}
                </div>
            </div>
            {/* Chat */}
            <div className="bg-paper rounded-2xl border border-border-line flex flex-col">
                {active ? (
                    <>
                        <div className="px-4 py-3 border-b border-border-line flex items-center gap-3">
                            <Avatar u={active} size={36} />
                            <div className="font-medium">{active.full_name}</div>
                        </div>
                        <div className="flex-1 overflow-y-auto p-4 space-y-3">
                            {messages.map((m) => {
                                const mine = m.from_user === me.id;
                                return (
                                    <div key={m.id} className={`flex ${mine ? "justify-end" : "justify-start"}`}>
                                        <div className={`max-w-[70%] px-4 py-2 rounded-2xl text-sm ${mine ? "bg-bordeaux text-white rounded-tr-sm" : "bg-surface text-ink rounded-tl-sm"}`}>
                                            {m.text}
                                        </div>
                                    </div>
                                );
                            })}
                            <div ref={endRef} />
                        </div>
                        <div className="p-3 border-t border-border-line flex gap-2">
                            <input data-testid="msg-input" value={text} onChange={(e) => setText(e.target.value)} onKeyDown={(e) => e.key === "Enter" && send()} placeholder="Scrivi un messaggio…" className="input-clean flex-1" />
                            <button data-testid="msg-send" onClick={send} className="btn-primary"><Send size={16} /></button>
                        </div>
                    </>
                ) : (
                    <div className="flex-1 flex items-center justify-center text-muted text-sm">Seleziona una conversazione</div>
                )}
            </div>
        </div>
    );
};

export default Messages;
